import { useState, useRef } from "react";
import { logger } from "../../../helper/logger";
import L from "../../../helper/CustomLeaflet";
import "leaflet.markercluster";
import polyline from "@mapbox/polyline";
import Route from "../../../services/Route";
import { routeColors } from "../../../components/colors";
import { CustomTooltip } from "../../../components/common/Tooltip/index";
import {
  LeafletDriverAnalyticsIMEIIcon,
  LeafletDriverStackIcon,
  LeafletFocusedOrderPickupIcon,
  LeafletGroupShipmentPickupIcon,
  LeafletGroupShipmentPickupIconOneDigit,
  LeafletShipmentStackIconTwoDigit,
  LeafletOrderPickupIcon,
  LeafletShipmentLocationIcon,
  LeafletShipmentStackIconOneDigit,
} from "../../../components/common/leaflet-icons";
import { getDriverIcon } from "../common/getDriverIcon";
import { toast } from "react-toastify";

export default function useLeafletMap({
  selectedShipmentIds,
  setSelectedShipmentIds,
  selectedDriverId,
  setSelectedDriverId,
  setSelectedDriver,
  filters,
  drivers,
  shipments,
  selectedShipments,
}) {
  const [map, setMap] = useState(null);
  const [markerClusterShipments, setMarkerClusterShipments] = useState(null);
  const [markerClusterDrivers, setMarkerClusterDrivers] = useState(null);
  const [shipmentMarkers, setShipmentMarkers] = useState([]);
  const [driverMarkers, setDriverMarkers] = useState([]);
  const [dropoffMarkers, setDropoffMarkers] = useState([]);
  const [driverMarkersMap, setDriverMarkersMap] = useState({});
  const [routeList, setRouteList] = useState([]);
  const [directionMarkers, setDirectionMarkers] = useState([]);
  const [limitMessage, setLimitMessage] = useState("");
  const [isDropoffMode, setIsDropoffMode] = useState(false);
  const [clusterSelection, setClusterSelection] = useState({
    open: false,
    position: null,
    shipmentIds: [],
  });
  const hubColorMap = useRef({});
  const routeColor = routeColors[4];

  const handleShipmentSelect = (id) => {
    setSelectedShipmentIds((prevState) => {
      if (prevState.includes(id)) {
        return prevState.filter((item) => item !== id);
      } else if (prevState.length < 30) {
        return [id, ...prevState]; // prepend so the last selected shows on top (matches the list handler)
      } else {
        setLimitMessage("Limit reached: You can only select up to 30 orders.");
        return prevState;
      }
    });
  };

  const isShipmentSelected = (id) => {
    return selectedShipmentIds.includes(id);
  };

  const onMarkerClick = (marker, type = "shipment", e) => {
    const id = marker.id;
    if (type === "shipment") {
      handleShipmentSelect(id);
    } else if (type === "dropoff") {
      handleShipmentSelect(id);
    } else {
      setSelectedDriverId((prevSelectedId) => {
        if (prevSelectedId === id) {
          setSelectedDriver(null);
          return null;
        } else {
          const driver = drivers.find((item) => item?.id === id);
          setSelectedDriver(driver);
          return id;
        }
      });
    }
  };

  const removeMapDecorations = (decorationList) => {
    decorationList.forEach((decoration) => {
      map.removeLayer(decoration);
    });
  };

  const showDriverMarkers = () => {
    try {
      if (map && drivers && drivers?.length >= 0) {
        const markersCluster = window.L.markerClusterGroup({
          spiderfyOnMaxZoom: false,
          showCoverageOnHover: false,
          zoomToBoundsOnClick: true,
          disableClusteringAtZoom: 10,
          iconCreateFunction: function (cluster) {
            let count = cluster.getChildCount();
            return LeafletDriverStackIcon(count);
          },
        });
        // remove old layer
        if (markerClusterDrivers) {
          removeMapDecorations([markerClusterDrivers]);
        }
        driverMarkers?.map((marker) => {
          // remove icons that are no longer in viewport
          if (!drivers.find((d) => d.id === marker.id)) {
            removeMapDecorations([marker]);
            delete driverMarkersMap[marker.id];
            setDriverMarkers((old) => old.filter((d) => d.id !== marker.id));
          }
        });

        drivers?.map((driver) => {
          showDriverMarker(driver, markersCluster);
        });

        map.addLayer(markersCluster);
        setMarkerClusterDrivers(markersCluster);
      }
    } catch (error) {
      logger("error", error);
    }
  };

  const showDriverMarker = (driver, driverMarkersCluster) => {
    if (!filters.driver.showOnMap) {
      // remove driverMarkers if any

      if (driverMarkers && driverMarkers.length > 0) {
        removeMapDecorations(driverMarkers);
        removeMapDecorations([markerClusterDrivers]);
      }
      setDriverMarkers([]);
      setMarkerClusterDrivers(null);
      setDriverMarkersMap({});
      return;
    }

    // if driver is already in the map, animate it, and move to new position
    if (driverMarkersMap[driver.id]) {
      const marker = driverMarkersMap[driver.id];
      marker.slideTo([driver.latitude, driver.longitude], {
        duration: 300,
      });
      marker.setIcon(getDriverIcon(driver, selectedDriverId));
      driverMarkersCluster.addLayer(marker);
      return;
    }

    // if some drivers are selected, gray icon will be chosen
    if (driver?.latitude && driver?.longitude) {
      let newMarker = window.L.marker([driver?.latitude, driver?.longitude], {
        icon: getDriverIcon(driver, selectedDriverId),
        zIndexOffset: 1000,
      }).on("click", (e) => {
        onMarkerClick(driver, "driver");
      });
      newMarker.id = driver.id;
      driverMarkersCluster.addLayer(newMarker);
      setDriverMarkersMap((prev) => ({
        ...prev,
        [driver.id]: newMarker,
      }));

      setDriverMarkers((prevMarkerList) => [...prevMarkerList, newMarker]);
    }
  };

  const closeClusterSelection = () => {
    setClusterSelection((prev) => ({ ...prev, open: false }));
  };

  const showShipmentMarkers = () => {
    try {
      if (map && shipments && shipments?.length >= 0) {
        if (markerClusterShipments) {
          removeMapDecorations([markerClusterShipments]);
        }
        removeMapDecorations(shipmentMarkers);
        setShipmentMarkers([]);

        const markersCluster = window.L.markerClusterGroup({
          spiderfyOnMaxZoom: false,
          showCoverageOnHover: false,
          zoomToBoundsOnClick: true,

          iconCreateFunction: function (cluster) {
            let count = cluster.getChildCount();
            if (map.getZoom() < 10) {
              return count > 30
                ? LeafletShipmentStackIconTwoDigit(count)
                : LeafletShipmentStackIconOneDigit(count);
            } else if (count < 30) {
              return LeafletGroupShipmentPickupIconOneDigit(count);
            }
            return LeafletGroupShipmentPickupIcon(count);
          },
        });

        shipments?.map((shipment) => {
          // check whether shipmentMarkers list has not obj with id === shipment.id
          const isShipmentMarkerExists = shipmentMarkers.some(
            (marker) => marker.id === shipment.id
          );
          if (!isShipmentMarkerExists) {
          }
          // Your logic here if the marker doesn't exist
          showShipmentMarker(shipment, markersCluster);
        });

        markersCluster.on("clusterclick", function (a) {
          if (a.layer._zoom > 10) {
            const markers = a.layer.getAllChildMarkers();
            const shipmentIds = markers.map((marker) => marker.id).filter(Boolean);
            const rect = map.getContainer().getBoundingClientRect();
            setClusterSelection({
              open: true,
              position: {
                top: Math.round(rect.top + 20),
                left: Math.round(rect.left + 120),
              },
              shipmentIds,
            });
          }
        });

        map.addLayer(markersCluster);
        setMarkerClusterShipments(markersCluster);
      }
    } catch (error) {
      logger("error", error);
    }
  };

  const showShipmentMarker = (shipment, markersCluster) => {
    if (!filters.shipment.showOnMap) {
      // remove shipmentMarkers if any
      if (shipmentMarkers && shipmentMarkers.length > 0) {
        removeMapDecorations(shipmentMarkers);
        removeMapDecorations([markerClusterShipments]);

        setShipmentMarkers([]);
        setMarkerClusterShipments(null);
      }
      return;
    }

    if (shipment.origin) {
      let markerIcon;
      if (selectedShipmentIds?.indexOf(shipment.id) !== -1) {
        markerIcon = LeafletFocusedOrderPickupIcon;
      } // selected shipment icon
      else {
        markerIcon = LeafletOrderPickupIcon;
      } // unselected shipment icon

      let newMarker = window.L.marker(
        [shipment.origin.latitude, shipment.origin.longitude],
        {
          icon: markerIcon,
          name: shipment.id,
        }
      ).on("click", (e) => onMarkerClick(shipment, "shipment", e));

      newMarker.id = shipment.id;
      newMarker.type = "shipment";

      markersCluster.addLayer(newMarker);
      setShipmentMarkers((prevMarkerList) => [...prevMarkerList, newMarker]);
    }
  };

  const handleUpdateSelectedShipmentGroupIcon = (id) => {
    const selectedMarker = shipmentMarkers.find((e) => e.id === id);
    if (selectedShipmentIds?.indexOf(id) !== -1) {
      let visibleOne = markerClusterShipments.getVisibleParent(selectedMarker);
      console.log("sel", visibleOne);
      visibleOne.setIcon(LeafletDriverAnalyticsIMEIIcon);
    }
  };

  const addRouteMarker = (marker, markerouteColor, isCompleted) => {
    let pickupMarker = window.L.marker([marker.latitude, marker.longitude], {
      icon: LeafletShipmentLocationIcon,
    }).addTo(map);

    // bind label to marker
    pickupMarker.bindTooltip(
      CustomTooltip(
        marker.type,
        marker.sequence,
        isCompleted ? "finished" : "notFinished",
        markerouteColor
      ),
      {
        permanent: true,
        direction: "center",
        offset: [12, -17],
        class: "rounded-full",
        style: "background: transparent",
      }
    );
    // assign id to differentiate from other markers
    pickupMarker.id = marker.id;

    // add markers to the list
    setDirectionMarkers((prevDirectionMarkers) => [
      ...prevDirectionMarkers,
      pickupMarker,
    ]);
  };

  const addDriverMarkerToRoute = (driver, markerouteColor, isCompleted) => {
    let driverMarker = window.L.marker([driver.latitude, driver.longitude], {
      // icon: getDriverIcon(driver, selectedDriverId)
    }).addTo(map);

    // bind label to marker
    driverMarker.bindTooltip(
      `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="55" viewBox="0 0 40 55" fill="none">
        <path d="M20.0001 54.1998L10.1275 37.0998L29.8728 37.0998L20.0001 54.1998Z" fill="green"/>
        <circle cx="20" cy="20" r="19" transform="rotate(-180 20 20)" fill="green" stroke="white" stroke-width="2"/>
        <text fill="white" xml:space="preserve" style="white-space: pre" font-family="Roboto" font-size="9" font-weight="bold" letter-spacing="0.4px"><tspan x="5.30889" y="21.5762">Driver</tspan></text>
        </svg>`,
      {
        permanent: true,
        direction: "center",
        iconSize: [40, 58],
        iconAnchor: [20, 28],
        class: "rounded-full",
        style: "background: transparent",
      }
    );
    // assign id to differentiate from other markers
    driverMarker.id = driver.id;

    // add markers to the list
    setDirectionMarkers((prevDirectionMarkers) => [
      ...prevDirectionMarkers,
      driverMarker,
    ]);
  };

  const showDriverRouteHistory = (driverId, from, to) => {
    return {};
    Route.getRouteHistory({
      driverId: driverId,
      from: from,
      to: to,
    }).then((res) => {
      logger("GOT DRIVER HISTORY ROUTE", res);
    });
  };

  const showShipmentListRoute = (shipmentList, areaCoverage) => {
    try {
      if (routeList?.length > 0) {
        // clear up old route icons & lines
        removeShipmentRouteLayers();
        return;
      }

      if (map && shipmentList) {
        toast.info("Showing route, please wait");

        let hasDriver = false;
        let driver = {};
        let driverData = {};
        // define driver data: (1st shipment Pickup for now)
        if (shipmentList[0]?.shipment?.isAssigned) {
          hasDriver = true;
          driver = shipmentList[0]?.shipment?.courier;
          driverData = {
            id: Number(shipmentList[0]?.shipment?.courier?.id),
            currentLocation: {
              latitude: shipmentList[0]?.shipment?.courier?.latitude,
              longitude: shipmentList[0]?.shipment?.courier?.longitude,
            },
          };
        } else {
          // set pickup point as driverData
          driverData = {
            id: Number(shipmentList[0]?.id),
            currentLocation: {
              latitude: shipmentList[0]?.origin?.latitude,
              longitude: shipmentList[0]?.origin?.longitude,
            },
          };
        }

        // define dropoffs & set markers
        const shipmentListObj = [];
        const orderStatuses = {};
        shipmentList.map((shipment) => {
          const dropOffs = [];

          let drop = {
            id: Number(shipment.id),
            latitude: shipment.destination.latitude,
            longitude: shipment.destination.longitude,
          };
          dropOffs.push(drop);
          orderStatuses[shipment.id] = shipment.orderStatus;

          const newShipmentObj = {
            id: Number(shipment.id),
            latitude: shipment.origin.latitude,
            longitude: shipment.origin.longitude,
            promise: 0, // idk what it is
            dropoffs: dropOffs,
            color: shipment.color,
          };

          shipmentListObj.push(newShipmentObj);
          orderStatuses[shipment.id] = shipment.shipment?.shipmentStatus;
        });

        let data = {
          driver: driverData,
          shipments: shipmentListObj,
          options: {
            g: true,
          },
        };

        Route.optimizeRoute(data).then((res) => {
          let bounds = L.latLngBounds();
          let routeListToAdd = [];

          const getColorForRoute = (route, index) => {
            if (route.type === 'start') return data.shipments[data.shipments.length - 1]?.color;
            if (route.type === 'end') return data.shipments[0]?.color;

            const sequenceDifference = index - route.sequence;
            const adjustedIndex = index - sequenceDifference - 1;
            const pairIndex = Math.max(0, data.shipments.length - Math.floor(adjustedIndex / 2) - 1);
            return data.shipments[pairIndex]?.color;
          };

          const createDashedPolyline = (polylineRes, id, colorNew) => {
            let dashRoute = L.polyline(polyline.decode(polylineRes)).addTo(map);
            dashRoute.setStyle({
              color: colorNew,
              weight: 2,
            });
            dashRoute.id = id;
            return dashRoute;
          };

          res?.forEach((route, index) => {
            const colorNew = getColorForRoute(route, index);
            const isCompleted = orderStatuses[route.id] === 'completed';

            const shouldAddMarker = route.type !== 'end' && route.type !== 'start';
            const shouldAddStartMarker =
              route.type === 'start' &&
              driverData.latitude !== shipmentListObj.latitude &&
              driverData.longitude !== shipmentListObj.longitude;

            if (shouldAddMarker || shouldAddStartMarker) {
              addRouteMarker(route, colorNew, isCompleted);
            }

            if (hasDriver) {
              addDriverMarkerToRoute(driver, routeColor);
            }

            const polylineRes = route?.geometry;
            if (polylineRes && index !== res.length - 1 && route.type !== 'end') {
              const decodedRoute = polyline.decode(polylineRes);
              const newRoute = L.polyline(decodedRoute).addTo(map);

              newRoute.setStyle({
                color: colorNew,
                opacity: isCompleted ? '0.8' : '0.6',
                weight: 7,
                stroke: true,
                className: 'shadow',
                fill: areaCoverage == true ? true : false,
                fillColor: '#666',
                fillOpacity: 0.4
              });

              routeListToAdd.push(newRoute);
              bounds.extend(newRoute.getBounds());

              if (!isCompleted) {
                const dashed = createDashedPolyline(polylineRes, shipmentList[0].id, colorNew);
                routeListToAdd.push(dashed);
              }
            }
          });

          setRouteList((prev) => [...prev, ...routeListToAdd]);
        });

      }
    } catch (error) {
      logger("error setting shipment route", error);
    }
  };

  const selectMarkersWithinRegion = (region) => {
    if (filters.shipment.showOnMap) {
      shipmentMarkers.map((marker) => {
        if (region.getBounds().contains(marker.getLatLng())) {
          const id = marker.id;
          handleShipmentSelect(id);
        }
      });
    } else if (filters.dropoff.showOnMap) {
      dropoffMarkers.map((marker) => {
        if (region.getBounds().contains(marker.getLatLng())) {
          const id = marker.id;
          handleShipmentSelect(id);
        }
      });
    }
  };

  const fitMapToBounds = (map, coordinates) => {
    if (coordinates.length === 0) {
      return;
    }

    const bounds = coordinates.reduce((acc, coord) => {
      return acc.extend([coord.latitude, coord.longitude]);
    }, L.latLngBounds());

    routeList.map((route) => {
      bounds.extend(route.getBounds());
    });
    map.flyToBounds(bounds, { duration: 1 });
  };

  const removeShipmentRouteLayers = () => {
    routeList.forEach((route) => {
      if (selectedShipmentIds.indexOf(route.id) === -1) {
        map.removeLayer(route);
      }
      removeMapDecorations(routeList);
      setRouteList([]);
      removeMapDecorations(directionMarkers);
      setDirectionMarkers([]);
    });
  };

  const customIcon = (hubId, orderId, shipment, color, trackingNo, courierId) => {

    return L.divIcon({
      html: `<div class="tooltip-container-drop-off-map" style="margin-top:-29px; margin-left: -13px;"><span><svg
          xmlns="http://www.w3.org/2000/svg"
          version="1.1"
          width="26"
          height="28"
          viewBox="0 0 256 256">
            <g style=" stroke: none; stroke-width: 0; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: none; fill-rule: nonzero; opacity: 1;"
              transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)">
              <path
                d="M 45 0 c 15.103 0 27.389 12.287 27.389 27.389 C 72.389 46.616 46.147 66.607 45 90 c -1.147 -23.393 -27.389 -43.384 -27.389 -62.611 C 17.611 12.287 29.897 0 45 0 z"
                style=" stroke: ${shipment ? "black" : "white"}; stroke-width: 1; fill: ${color}; fill-rule: nonzero; opacity: 1;"
                transform=" matrix(1 0 0 1 0 0) "
                stroke-linecap="round"/>
              <circle cx="45.004999999999995" cy="26.575000000000003" r="9.205"
                style="stroke: ${shipment ? "black" : "white"}; stroke-width: 1; fill: ${shipment ? "orange" : "white"}; fill-rule: nonzero; opacity: 1;"
                transform="  matrix(1 0 0 1 0 0)"/>
            </g>
          </svg>
        </span><div class="tooltip-text-drop-off-map">${trackingNo}<span style="color:brown;" >${courierId != null ? "<br>Courier ID: " + courierId : ""}</span></div>
      </div>`,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      zIndexOffset: 10,
    });
  };

  const customIconWithNumber = (hubId, orderId, number, shipment, color, trackingNo, courierId) => {

    return L.divIcon({
      html: `<div class="selected-drop-offs-bounce-anime tooltip-container-drop-off-map" style="margin-top:-29px; margin-left: -13px;"><span><svg
              xmlns="http://www.w3.org/2000/svg"
              version="1.1"
              width="28"
              height="29"
              viewBox="0 0 256 256">
              <defs>
                  <filter id="f1" x="-10" y="-10" width="120" height="120">
                      <feOffset result="offOut" in="SourceAlpha" dx="2" dy="2" />
                      <feGaussianBlur result="blurOut" in="offOut" stdDeviation="3" />
                      <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
                  </filter>
              </defs>
              <g style="stroke: none; stroke-width: 0; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: none; fill-rule: nonzero; opacity: 1;"
                  transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)">
                  <path
                      d="M 45 0 c 15.103 0 27.389 12.287 27.389 27.389 C 72.389 46.616 46.147 66.607 45 90 c -1.147 -23.393 -27.389 -43.384 -27.389 -62.611 C 17.611 12.287 29.897 0 45 0 z"
                      style="stroke: ${shipment ? 'black' : 'white'}; stroke-width: 2; fill: ${color}; fill-rule: nonzero; opacity: 1;"
                      transform="matrix(1 0 0 1 0 0)"
                      stroke-linecap="round"/>
                  <text x="45" y="40" font-size="28" font-weight="bold" text-anchor="middle" fill="white" filter="url(#f1)">${number}</text>
              </g>
          </svg>
        </span><div class="tooltip-text-drop-off-map">${trackingNo}<span style="color:brown;" >${courierId != null ? "<br>Courier ID: " + courierId : ""}</span></div>
      </div>`,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      zIndexOffset: 10,
    });
  };

  const controlDropoffMode = (isShown) => {
    if (!isShown) {
      setIsDropoffMode(false);

      if (dropoffMarkers?.length > 0) clearDropoffMarkers();

      return;
    }
    setIsDropoffMode(true);
    handleShowDropoffs();
  };

  const handleShowDropoffs = () => {
    if (dropoffMarkers?.length > 0) clearDropoffMarkers();

    const listDropOffs = shipments
      .filter(order => order.addressStatus === 'updated')
      .map((order) => {
        if (!selectedShipmentIds.includes(order.id))
          return {
            latitude: order.destination.latitude,
            longitude: order.destination.longitude,
            id: order?.id,
            number: order?.id,
            hubId: order?.hub?.id,
            shipment: order?.shipment ? true : false,
            color: order?.color,
            tracking_no: order?.tracking_no,
            knight: order?.knight?.id || null,
          };
      })
      .filter(Boolean);
    listDropOffs?.forEach((dropOff) => {
      const dropoffMarker = L.marker([dropOff.latitude, dropOff.longitude], {
        icon: customIcon(dropOff?.hubId, dropOff?.id, dropOff?.shipment, dropOff?.color, dropOff?.tracking_no, dropOff?.knight),
      })
        .addTo(map)
        .on("click", (e) => onMarkerClick(dropOff, "dropoff", e));

      dropoffMarker.id = dropOff.id;
      setDropoffMarkers((old) => [...old, dropoffMarker]);
    });
    const listDirNSelected = selectedShipments
      .filter(order => order.addressStatus === 'updated')
      .map((order) => ({
        latitude: order.destination.latitude,
        longitude: order.destination.longitude,
        id: order.id,
        number: order?.number,
        hubId: order.hub.id,
        shipment: order?.shipment ? true : false,
        color: order?.color,
        tracking_no: order?.tracking_no,
        knight: order?.knight?.id || null,
      }));

    listDirNSelected?.forEach((dropOff) => {
      const dropoffMarker = L.marker([dropOff.latitude, dropOff.longitude], {
        icon: customIconWithNumber(dropOff?.hubId, dropOff?.id, dropOff?.number, dropOff?.shipment, dropOff?.color, dropOff?.tracking_no, dropOff?.knight
        ),
      })
        .addTo(map)
        .on("click", (e) => onMarkerClick(dropOff, "dropoff", e));

      dropoffMarker.id = dropOff.id;
      setDropoffMarkers((old) => [...old, dropoffMarker]);
    });
  };

  const clearDropoffMarkers = () => {
    removeMapDecorations(dropoffMarkers);
    setDropoffMarkers([]);
  };

  return {
    map,
    setMap,
    routeList,
    setRouteList,
    showShipmentMarkers,
    showDriverMarkers,
    showShipmentListRoute,
    removeMapDecorations,
    directionMarkers,
    setDirectionMarkers,
    selectMarkersWithinRegion,
    fitMapToBounds,
    removeShipmentRouteLayers,
    handleShowDropoffs,
    isDropoffMode,
    clearDropoffMarkers,
    controlDropoffMode,
    dropoffMarkers,
    limitMessage,
    setLimitMessage,
    clusterSelection,
    closeClusterSelection,
    handleShipmentSelect,
    isShipmentSelected,
  };
}